/**
 * order-list-sort.ts — FE helpers for URL `sort=` state on the order list.
 * Keys match the BE allowlist in `server/.../order-list-sort.ts` and `orderColumns.ts`.
 */

import { ORDER_COLUMNS, type OrderColumnKey } from './orderColumns';

const SORTABLE_KEYS = new Set<OrderColumnKey>(ORDER_COLUMNS.map((col) => col.key));

/** Parsed sort from `?sort=field` or `?sort=-field`. */
export type OrderListSortState = {
  key: OrderColumnKey;
  direction: 'asc' | 'desc';
};

/**
 * Parses the URL sort param. Returns null when absent or not a known column key.
 */
export function parseOrderListSortParam(raw: string | null | undefined): OrderListSortState | null {
  if (!raw?.trim()) return null;

  const trimmed = raw.trim();
  const direction: OrderListSortState['direction'] = trimmed.startsWith('-') ? 'desc' : 'asc';
  const keyRaw = direction === 'desc' ? trimmed.slice(1) : trimmed;

  if (!keyRaw || !SORTABLE_KEYS.has(keyRaw as OrderColumnKey)) return null;

  return { key: keyRaw as OrderColumnKey, direction };
}

/**
 * Cycles sort for a column header click: first click asc, second desc, then toggles.
 */
export function nextOrderListSortParam(
  current: string | null | undefined,
  column: OrderColumnKey,
): string {
  const parsed = parseOrderListSortParam(current ?? undefined);
  if (parsed?.key === column) {
    return parsed.direction === 'asc' ? `-${column}` : column;
  }
  return column;
}

/** Maps parsed sort to `aria-sort` for the active column header. */
export function ariaSortForColumn(
  column: OrderColumnKey,
  sort: OrderListSortState | null,
): 'ascending' | 'descending' | 'none' {
  if (!sort || sort.key !== column) return 'none';
  return sort.direction === 'asc' ? 'ascending' : 'descending';
}
